import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { TextField } from '@/shared/forms/TextField';
import { Button } from '@/shared/ui/Button';

import { useRegister } from '../hooks/useRegister';
import { registerSchema } from '../validation/register.schema';

/**
 * Step 1 of signup: collect account details, create the (unverified) account,
 * and hand the email up so the parent can show the OTP step.
 *
 * @param {{ onRegistered: (email: string) => void }} props
 */
export function RegisterForm({ onRegistered }) {
  const registerMutation = useRegister();
  const form = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      passwordConfirm: '',
      phone: '',
      address: '',
      city: '',
      country: '',
    },
    mode: 'onBlur',
  });

  const onSubmit = form.handleSubmit(async (values) => {
    const payload = { ...values };
    delete payload.passwordConfirm;
    try {
      await registerMutation.mutateAsync(payload);
      onRegistered(payload.email);
    } catch (error) {
      if (error.status === 422 && error.details) {
        for (const [field, messages] of Object.entries(error.details)) {
          form.setError(field, { message: Array.isArray(messages) ? messages[0] : messages });
        }
        return;
      }
      form.setError('root', {
        message: error.message || 'Unable to create your account right now. Please try again.',
      });
    }
  });

  const { errors } = form.formState;

  return (
    <form onSubmit={onSubmit} noValidate className="grid gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Full name"
          required
          autoComplete="name"
          error={errors.name?.message}
          {...form.register('name')}
        />
        <TextField
          label="Email"
          required
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          error={errors.email?.message}
          {...form.register('email')}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Password"
          required
          type="password"
          autoComplete="new-password"
          error={errors.password?.message}
          {...form.register('password')}
        />
        <TextField
          label="Confirm password"
          required
          type="password"
          autoComplete="new-password"
          error={errors.passwordConfirm?.message}
          {...form.register('passwordConfirm')}
        />
      </div>
      <TextField
        label="Phone"
        required
        autoComplete="tel"
        error={errors.phone?.message}
        {...form.register('phone')}
      />
      <TextField
        label="Address"
        required
        autoComplete="street-address"
        error={errors.address?.message}
        {...form.register('address')}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="City"
          required
          autoComplete="address-level2"
          error={errors.city?.message}
          {...form.register('city')}
        />
        <TextField
          label="Country"
          required
          autoComplete="country-name"
          error={errors.country?.message}
          {...form.register('country')}
        />
      </div>

      {errors.root && (
        <p
          role="alert"
          className="rounded-lg bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300"
        >
          {errors.root.message}
        </p>
      )}

      <Button type="submit" loading={registerMutation.isPending} className="w-full">
        Create account
      </Button>
    </form>
  );
}
